import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { TESTIDS } from "@/constants/testIds";
import { ROLES, APPROVER_ROLES, JADWAL_KERJA } from "@/constants/roles";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { UserPlus, Pencil, Trash2, KeyRound, Upload, Search, ChevronLeft, ChevronRight, Link2 } from "lucide-react";

const emptyForm = {
  email: "", password: "", name: "", npp: "", portofolio: "", role: "staff",
  unit_id: "", managed_unit_ids: [],
  office_ids: [], approver_ids: [], jadwal_kerja: "WFO",
  annual_leave_quota: 12,
  wfa_permanent: false,
};

export default function AdminUsers() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(100);
  const [search, setSearch] = useState("");
  const [unitFilter, setUnitFilter] = useState("");
  const [offices, setOffices] = useState([]);
  const [units, setUnits] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [resetting, setResetting] = useState(null);
  const [newPass, setNewPass] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [importResult, setImportResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (search.trim()) params.q = search.trim();
      if (unitFilter) params.unit_id = unitFilter;
      const [u, o, un, all] = await Promise.all([
        api.get("/admin/users", { params }),
        api.get("/offices"),
        api.get("/work-units"),
        api.get("/admin/users/all"),
      ]);
      setItems(u.data.items); setTotal(u.data.total);
      setOffices(o.data); setUnits(un.data);
      setAllUsers(all.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, unitFilter]);
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({
      email: u.email, password: "", name: u.name, npp: u.npp || "",
      portofolio: u.portofolio || "",
      role: u.role,
      unit_id: u.unit_id || "",
      managed_unit_ids: u.managed_unit_ids || [],
      office_ids: u.office_ids || [], approver_ids: u.approver_ids || [],
      jadwal_kerja: u.jadwal_kerja || "WFO",
      annual_leave_quota: u.annual_leave_quota ?? 12,
      wfa_permanent: !!u.wfa_permanent,
    });
    setDialogOpen(true);
  };

  const submitForm = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name, npp: form.npp, portofolio: form.portofolio,
      role: form.role,
      unit_id: form.unit_id || null,
      managed_unit_ids: form.role === "admin_unit" ? form.managed_unit_ids : [],
      office_ids: form.office_ids,
      approver_ids: form.approver_ids,
      jadwal_kerja: form.jadwal_kerja,
      annual_leave_quota: Number(form.annual_leave_quota) || 0,
      wfa_permanent: !!form.wfa_permanent,
    };
    try {
      if (editing) {
        await api.patch(`/admin/users/${editing.user_id}`, payload);
        toast.success("User diperbarui.");
      } else {
        await api.post("/admin/users", { ...payload, email: form.email, password: form.password });
        toast.success("User dibuat.");
      }
      setDialogOpen(false); load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Gagal simpan.");
    }
  };

  const doDelete = async (u) => {
    if (!window.confirm(`Hapus ${u.name}?`)) return;
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      toast.success("User dihapus."); load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Gagal hapus."); }
  };

  const doReset = async () => {
    if (!newPass || newPass.length < 6) return toast.error("Password minimal 6 karakter.");
    try {
      await api.post(`/admin/users/${resetting.user_id}/reset-password`, { new_password: newPass });
      toast.success("Password direset.");
      setResetting(null); setNewPass("");
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Gagal reset."); }
  };

  const doImport = async () => {
    if (!csvText.trim()) return toast.error("Isi CSV terlebih dahulu.");
    try {
      const { data } = await api.post("/admin/users/import", { csv: csvText });
      setImportResult(data);
      toast.success(`Impor: ${data.created} baru, ${data.updated} diperbarui, ${data.errors.length} error`);
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Gagal impor."); }
  };

  const toggleId = (list, id) => list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const approverCandidates = useMemo(
    () => allUsers.filter((u) =>
      APPROVER_ROLES.includes(u.role) &&
      u.role !== "admin" &&
      u.role !== "admin_unit" &&
      u.user_id !== editing?.user_id
    ),
    [allUsers, editing]
  );

  return (
    <div data-testid={TESTIDS.adminUsersPage} className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="label-tiny">Manajemen Karyawan</div>
          <h1 className="font-display text-3xl sm:text-5xl font-black tracking-tighter mt-2">Daftar Karyawan</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button data-testid="import-users-btn" onClick={() => { setImportOpen(true); setImportResult(null); setCsvText(""); }}
            className="inline-flex items-center gap-2 border border-black/40 px-4 py-2.5 hover:bg-black hover:text-white text-sm font-semibold">
            <Upload className="w-4 h-4" /> Impor CSV
          </button>
          <button data-testid={TESTIDS.createUserBtn} onClick={openCreate}
            className="inline-flex items-center gap-2 bg-klein text-white px-5 py-2.5 hover:bg-black text-sm font-semibold">
            <UserPlus className="w-4 h-4" /> Tambah Karyawan
          </button>
        </div>
      </div>

      {/* Search + Filter */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-3xl">
        <div className="sm:col-span-2 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input data-testid="user-search"
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari nama, NPP, atau email..."
            className="w-full border border-black/20 pl-10 pr-3 py-2.5 bg-white text-sm rounded-none" />
        </div>
        <select data-testid="user-unit-filter" value={unitFilter}
          onChange={(e) => { setUnitFilter(e.target.value); setPage(1); }}
          className="w-full border border-black/20 px-3 py-2.5 bg-white text-sm rounded-none">
          <option value="">Semua Unit Kerja</option>
          {units.map((u) => <option key={u.unit_id} value={u.unit_id}>{u.code ? `[${u.code}] ` : ""}{u.name}</option>)}
        </select>
      </div>

      <div className="border border-black/10 bg-white overflow-x-auto">
        <table data-testid={TESTIDS.usersTable} className="w-full text-sm min-w-[1200px]">
          <thead className="bg-black text-white text-left">
            <tr>
              <Th>NPP</Th><Th>Nama</Th><Th>Email</Th><Th>Unit Kerja</Th><Th>Role</Th>
              <Th>Jadwal</Th><Th>WFA</Th><Th>Kantor</Th><Th>Kuota</Th><Th>SPK</Th><Th>Aksi</Th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={11} className="p-8 text-center text-gray-500">Memuat...</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={11} className="p-8 text-center text-gray-500">Tidak ada karyawan.</td></tr>
            )}
            {items.map((u) => {
              const officeNames = offices.filter((o) => (u.office_ids || []).includes(o.office_id)).map((o) => o.name);
              const unit = units.find((x) => x.unit_id === u.unit_id);
              return (
                <tr key={u.user_id} className="border-t border-black/10 hover:bg-gray-50">
                  <Td className="font-mono text-xs">{u.npp || <span className="text-gray-400">—</span>}</Td>
                  <Td className="font-semibold">{u.name}</Td>
                  <Td className="text-gray-600 text-xs">{u.email}</Td>
                  <Td className="text-xs">{unit ? unit.name : <span className="text-gray-400">—</span>}</Td>
                  <Td>
                    <span className="px-2 py-1 border border-black/20 text-[10px] font-bold uppercase tracking-widest whitespace-nowrap">
                      {ROLES[u.role] || u.role}
                    </span>
                  </Td>
                  <Td>
                    <span className="px-2 py-0.5 bg-klein/10 text-klein text-xs font-semibold border border-klein/30">
                      {JADWAL_KERJA[u.jadwal_kerja] || u.jadwal_kerja || "WFO"}
                    </span>
                  </Td>
                  <Td>
                    {u.wfa_permanent
                      ? <span className="px-2 py-0.5 bg-signal-ok text-white text-[10px] font-bold uppercase tracking-widest">Aktif</span>
                      : <span className="px-2 py-0.5 border border-black/20 text-[10px] font-bold uppercase tracking-widest text-gray-500">Non-aktif</span>}
                  </Td>
                  <Td className="text-xs">{officeNames.join(", ") || <span className="text-gray-400">—</span>}</Td>
                  <Td className="font-mono text-xs">{u.annual_leave_quota ?? 0}</Td>
                  <Td>
                    {u.portofolio ? (
                      <a href={u.portofolio} target="_blank" rel="noreferrer" className="text-klein hover:underline inline-flex items-center gap-1 text-xs">
                        <Link2 className="w-3 h-3" /> Buka
                      </a>
                    ) : <span className="text-gray-400 text-xs">—</span>}
                  </Td>
                  <Td>
                    <div className="flex items-center gap-1">
                      <button onClick={() => openEdit(u)} className="border border-black/20 hover:bg-black hover:text-white p-1.5" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => setResetting(u)} className="border border-black/20 hover:bg-black hover:text-white p-1.5" title="Reset Password"><KeyRound className="w-3.5 h-3.5" /></button>
                      <button onClick={() => doDelete(u)} className="border border-black/20 hover:bg-signal-late hover:text-white p-1.5" title="Hapus"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-sm">
        <div className="text-gray-600">Total: <strong>{total}</strong> karyawan</div>
        <div className="flex items-center gap-2">
          <button data-testid="pagination-prev" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
            className="border border-black/20 px-3 py-1.5 hover:bg-black hover:text-white disabled:opacity-40 inline-flex items-center gap-1">
            <ChevronLeft className="w-4 h-4" /> Prev
          </button>
          <div className="px-3">Halaman {page} / {totalPages}</div>
          <button data-testid="pagination-next" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
            className="border border-black/20 px-3 py-1.5 hover:bg-black hover:text-white disabled:opacity-40 inline-flex items-center gap-1">
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-none max-w-3xl border-black/20 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">
              {editing ? "Edit Karyawan" : "Tambah Karyawan"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitForm} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FF label="Nama Lengkap" required>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="input" />
              </FF>
              <FF label="NPP">
                <input value={form.npp} onChange={(e) => setForm({ ...form, npp: e.target.value })} className="input font-mono" placeholder="mis. EPS-0001" />
              </FF>
            </div>
            <FF label="Email" required>
              <input type="email" value={form.email} disabled={!!editing}
                onChange={(e) => setForm({ ...form, email: e.target.value })} required className="input disabled:bg-gray-100" />
            </FF>
            {!editing && (
              <FF label="Password (min. 6)" required>
                <input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} className="input font-mono" />
              </FF>
            )}
            <FF label="SPK">
              <input type="url" value={form.portofolio} onChange={(e) => setForm({ ...form, portofolio: e.target.value })}
                className="input font-mono" placeholder="..." />
            </FF>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <FF label="Role">
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="input">
                  {Object.entries(ROLES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </FF>
              <FF label="Jadwal Kerja">
                <select value={form.jadwal_kerja} onChange={(e) => setForm({ ...form, jadwal_kerja: e.target.value })} className="input">
                  {Object.entries(JADWAL_KERJA).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </FF>
              <FF label="Unit Kerja">
                <select value={form.unit_id || ""} onChange={(e) => setForm({ ...form, unit_id: e.target.value })} className="input">
                  <option value="">— Tidak di-assign —</option>
                  {units.map((u) => <option key={u.unit_id} value={u.unit_id}>{u.code ? `[${u.code}] ` : ""}{u.name}</option>)}
                </select>
              </FF>
            </div>
            <FF label="Kuota Cuti Tahunan (hari)">
              <input type="number" min={0} value={form.annual_leave_quota}
                onChange={(e) => setForm({ ...form, annual_leave_quota: e.target.value })} className="input" />
            </FF>

            <FF label="WFA Permanen (bebas dari radius kantor)">
              <label className="flex items-center gap-3 border border-black/20 px-3 py-2.5 bg-white cursor-pointer">
                <input type="checkbox" data-testid="user-wfa-permanent"
                  checked={!!form.wfa_permanent}
                  onChange={(e) => setForm({ ...form, wfa_permanent: e.target.checked })}
                  className="w-4 h-4" />
                <span className="text-sm">
                  {form.wfa_permanent
                    ? "Aktif — karyawan bebas absen dari mana saja tanpa cek radius."
                    : "Non-aktif — karyawan wajib berada dalam radius kantor saat absen."}
                </span>
              </label>
            </FF>

            {form.role === "admin_unit" && (
              <FF label="Unit Kerja yang Dikelola (multi-select)">
                <div className="border border-black/20 p-3 max-h-32 overflow-y-auto space-y-1">
                  {units.map((u) => (
                    <label key={u.unit_id} className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={form.managed_unit_ids.includes(u.unit_id)}
                        onChange={() => setForm({ ...form, managed_unit_ids: toggleId(form.managed_unit_ids, u.unit_id) })} />
                      <span>{u.name}</span>
                    </label>
                  ))}
                </div>
              </FF>
            )}

            <FF label="Kantor Ditugaskan">
              <div className="border border-black/20 p-3 max-h-32 overflow-y-auto space-y-1">
                {offices.length === 0 && <div className="text-xs text-gray-400">Belum ada kantor.</div>}
                {offices.map((o) => (
                  <label key={o.office_id} className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={form.office_ids.includes(o.office_id)}
                      onChange={() => setForm({ ...form, office_ids: toggleId(form.office_ids, o.office_id) })} />
                    <span>{o.name} <span className="text-xs text-gray-500">({o.radius_meters}m)</span></span>
                  </label>
                ))}
              </div>
            </FF>

            <FF label="Approver Berjenjang (Staff tidak muncul di sini)">
              <div className="border border-black/20 p-3 max-h-40 overflow-y-auto space-y-1">
                {approverCandidates.length === 0 && (
                  <div className="text-xs text-gray-400">Belum ada karyawan dengan role Asisten Manager ke atas.</div>
                )}
                {approverCandidates.map((u) => {
                  const idx = form.approver_ids.indexOf(u.user_id);
                  return (
                    <label key={u.user_id} className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={idx >= 0}
                        onChange={() => setForm({ ...form, approver_ids: toggleId(form.approver_ids, u.user_id) })} />
                      <span>
                        {idx >= 0 && <span className="mr-1 inline-block bg-klein text-white text-[10px] px-1.5 py-0.5">#{idx + 1}</span>}
                        {u.name} <span className="text-xs text-gray-500">({ROLES[u.role]})</span>
                      </span>
                    </label>
                  );
                })}
              </div>
              <div className="text-xs text-gray-500 mt-1">Approval mengalir sesuai urutan Anda mencentang.</div>
            </FF>

            <DialogFooter>
              <button type="button" onClick={() => setDialogOpen(false)} className="border border-black/20 px-4 py-2 hover:bg-gray-100 text-sm">Batal</button>
              <button type="submit" className="bg-klein text-white px-5 py-2 hover:bg-black text-sm font-semibold">
                {editing ? "Simpan" : "Buat"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset password */}
      <Dialog open={!!resetting} onOpenChange={(o) => !o && setResetting(null)}>
        <DialogContent className="rounded-none max-w-md border-black/20">
          <DialogHeader><DialogTitle className="font-display text-2xl">Reset Password</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="text-sm">Reset password untuk <strong>{resetting?.name}</strong> ({resetting?.email})</div>
            <FF label="Password Baru">
              <input type="text" value={newPass} onChange={(e) => setNewPass(e.target.value)} minLength={6} className="input font-mono" />
            </FF>
          </div>
          <DialogFooter>
            <button onClick={() => setResetting(null)} className="border border-black/20 px-4 py-2 text-sm">Batal</button>
            <button onClick={doReset} className="bg-klein text-white px-5 py-2 hover:bg-black text-sm font-semibold">Reset</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import CSV */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="rounded-none max-w-3xl border-black/20 max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">Impor Karyawan (CSV)</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-gray-700">
              Tempel isi file CSV di bawah. Kolom yang didukung (case-insensitive):{" "}
              <code className="bg-gray-100 px-1">npp, name, email, password, role, unit_name, unit_id, office_ids, jadwal_kerja, annual_leave_quota, portofolio</code>.
              Baris dengan email yang sudah ada akan diperbarui.
            </div>
            <div className="bg-gray-50 border border-black/10 p-3 text-xs font-mono whitespace-pre overflow-x-auto">
{`npp,name,email,password,role,unit_name,jadwal_kerja,annual_leave_quota,portofolio
EPS-001,Budi,budi@episi.app,pass1234,staff,Divisi Keuangan,WFO,12,https://linkedin.com/in/budi
EPS-002,Ani,ani@episi.app,pass1234,manager,Divisi IT,WFH,15,`}
            </div>
            <label htmlFor="csv-file" className="inline-flex items-center gap-2 border border-black/20 px-3 py-2 hover:bg-black hover:text-white cursor-pointer text-sm">
              <Upload className="w-4 h-4" /> Pilih file CSV
              <input id="csv-file" type="file" accept=".csv,text/csv" className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0]; if (!f) return;
                  const r = new FileReader(); r.onload = () => setCsvText(String(r.result || "")); r.readAsText(f);
                }} />
            </label>
            <textarea rows={8} value={csvText} onChange={(e) => setCsvText(e.target.value)}
              placeholder="…atau tempel CSV di sini"
              className="input font-mono text-xs" />
            {importResult && (
              <div className="border border-black/10 p-3 text-sm">
                <div>Berhasil dibuat: <strong className="text-signal-ok">{importResult.created}</strong></div>
                <div>Diperbarui: <strong className="text-klein">{importResult.updated}</strong></div>
                <div>Error: <strong className="text-signal-late">{importResult.errors?.length || 0}</strong></div>
                {importResult.errors?.length > 0 && (
                  <ul className="mt-2 max-h-40 overflow-y-auto text-xs">
                    {importResult.errors.map((e, i) => (
                      <li key={i} className="text-signal-late">Baris {e.row}: {e.error} {e.email ? `(${e.email})` : ""}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <button onClick={() => setImportOpen(false)} className="border border-black/20 px-4 py-2 text-sm">Tutup</button>
            <button data-testid="import-submit" onClick={doImport} className="bg-klein text-white px-5 py-2 hover:bg-black text-sm font-semibold">Impor</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <style>{`.input { width:100%; border: 1px solid rgba(0,0,0,0.2); padding: 10px 12px; background: white; font-size: 14px; } .input:focus { outline: 2px solid #002FA7; outline-offset: -2px; }`}</style>
    </div>
  );
}

function FF({ label, children, required }) {
  return <div><div className="label-tiny mb-2">{label}{required && <span className="text-signal-late"> *</span>}</div>{children}</div>;
}
const Th = ({ children }) => <th className="px-4 py-3 text-xs uppercase tracking-widest font-bold">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
